import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

export function AdminSettings() {
  const queryClient = useQueryClient()
  
  const { data: status } = useQuery({
    queryKey: ['admin', 'setup-status'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/setup-status`, { credentials: 'include' })
      return res.json()
    },
  })

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold mb-2">Settings</h1>
        <p className="text-gray-400">Manage your admin account security</p>
      </div>

      <div className="grid gap-6">
        <PasskeySettings 
          hasPasskey={status?.hasPasskey} 
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'setup-status'] })}
        />
        <PasswordSettings />
      </div>
    </div>
  )
}

function PasskeySettings({ hasPasskey, onUpdate }: { hasPasskey: boolean; onUpdate: () => void }) {
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const registerPasskey = async () => {
    setError('')
    setSuccess('')
    try {
      // Get registration options
      const optionsRes = await fetch(`${getApiUrl()}/admin/passkey/register-options`, {
        method: 'POST',
        credentials: 'include',
      })
      const options = await optionsRes.json()
      
      if (options.error) {
        throw new Error(options.error)
      }
      
      // Convert challenge from base64url
      options.challenge = base64UrlDecode(options.challenge)
      options.user.id = base64UrlDecode(options.user.id)
      
      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: options,
      }) as PublicKeyCredential
      
      if (!credential) {
        throw new Error('Failed to create credential')
      }
      
      const response = credential.response as AuthenticatorAttestationResponse
      
      // Send to server
      const registerRes = await fetch(`${getApiUrl()}/admin/passkey/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: arrayBufferToBase64Url(credential.rawId),
            response: {
              attestationObject: arrayBufferToBase64Url(response.attestationObject),
              clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
            },
            type: credential.type,
          },
        }),
      })
      
      const result = await registerRes.json()
      if (result.error) {
        throw new Error(result.error)
      }
      
      setSuccess('Passkey registered successfully!')
      onUpdate()
    } catch (err: any) {
      setError(err.message || 'Failed to register passkey')
    }
  }
  
  const deletePasskey = async () => {
    if (!confirm('Are you sure you want to remove your passkey? You will need to use password login.')) {
      return
    }
    
    try {
      const res = await fetch(`${getApiUrl()}/admin/passkey`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete passkey')
      setSuccess('Passkey removed')
      onUpdate()
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-purple-500 flex items-center justify-center text-2xl">
          🔐
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold">Passkey</h3>
          {hasPasskey ? (
            <p className="text-green-400 text-sm">Configured</p>
          ) : (
            <p className="text-gray-400 text-sm">Not configured</p>
          )}
        </div>
      </div>
      
      <p className="text-gray-400 text-sm mb-4">
        Passkeys use Face ID, Touch ID, or Windows Hello for secure passwordless login.
      </p>
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm mb-4">
          {success}
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          onClick={registerPasskey}
          className="btn btn-primary"
        >
          {hasPasskey ? 'Replace Passkey' : 'Add Passkey'}
        </button>
        
        {hasPasskey && (
          <button
            onClick={deletePasskey}
            className="btn bg-red-600/20 hover:bg-red-600/30 text-red-400"
          >
            Remove Passkey
          </button>
        )}
      </div>
    </div>
  )
}

function PasswordSettings() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const changeMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await fetch(`${getApiUrl()}/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Failed to change password')
      return result
    },
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setError('')
      setSuccess('Password changed successfully!')
    },
    onError: (err: Error) => {
      setError(err.message)
      setSuccess('')
    },
  })
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    
    changeMutation.mutate({ currentPassword, newPassword })
  }

  return (
    <div className="card">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">
          🔑
        </div>
        <div>
          <h3 className="text-xl font-semibold">Password</h3>
          <p className="text-gray-400 text-sm">Backup authentication method</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="input w-full"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm text-gray-400 mb-1">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="input w-full"
            minLength={8}
            required
          />
        </div>
        
        <div>
          <label className="block text-sm text-gray-400 mb-1">Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="input w-full"
            minLength={8}
            required
          />
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm">
            {success}
          </div>
        )}
        
        <button
          type="submit"
          disabled={changeMutation.isPending}
          className="btn btn-primary"
        >
          {changeMutation.isPending ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  )
}

// Helper functions
function base64UrlDecode(str: string): ArrayBuffer {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - base64.length % 4) % 4)
  const binary = atob(base64 + padding)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}
